import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import Button from "../../../../shared/button/button";
import { ComputerIcon } from "../../../../assets/icons/stepsIcons/ComputerIcon";
import { ConversationIcon } from "../../../../assets/icons/stepsIcons/ConversationIcon";
import UserIcon from "../../../../assets/icons/stepsIcons/UserIcon";
import { StepWrapper } from "../../../common/styledComponents/stepsWrapper";
import { useLanguage } from "../../../../hooks/useLanguage";
import Services from "../../../../API/PIservices";
import successIcon from "../../../../assets/icons/success.svg";

import {
  isContentGeorgian,
  YES_NO_VAL,
  YES_NO_QUESTIONS,
  SUBFORM_PREFIXES,
} from "../step3/utils";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useMutation } from "@tanstack/react-query";
import successEvent from "../../../../utils/helpers/successEvent";
import { useGlobalContext } from "../../../../context/globalContext";
import { SERVICE_TYPES } from "../../../../utils/constants/fieldErrors";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { clearPersistedIdentificationStatus } from "../../../../utils/constants/piFormSession";
import Loader from "../../../../shared/loaders/simpleLoader";

export const TextWrapper = styled.div`
  padding: 15px;
  text-align: center;
  line-height: 300%;

  .title {
    font-weight: bold;
    font-size: 18px;
  }
`;

const DesktopOnly = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    gap: 24px;
    color: #1f2937;
    line-height: 150%;
  }
`;

const MobileIntroText = styled.div`
  font-size: 14px;

  b {
    font-weight: 700;
  }

  a {
    color: #e6000f;
    font-weight: 700;
  }
`;

const MobileServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const MobileServiceItem = styled.div`
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;

  svg {
    max-width: 34px;
    height: auto;
  }

  b {
    font-weight: 700;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 600px) {
    border-top: 1px solid #e5e7eb;
    padding-top: 24px;
    margin-top: 8px;
    gap: 12px;

    button {
      min-width: 120px;
    }
  }

  @media (min-width: 768px) {
    justify-content: flex-end;
    margin-top: 24px;
  }

  @media (max-width: 360px) {
    flex-direction: column-reverse;
    align-items: center;

    button {
      width: 160px;
    }
  }
`;

function AboutServices({ clientTypeId, token = null }) {
  const [isSendedForm, setIsSendedForm] = useState(false);

  const { t } = useTranslation();
  const { contentLanguage } = useLanguage();
  const history = useHistory();
  const { handleSubmit } = useFormContext();

  const { catchErrorPIOnboarding, resetGlobalData } = useGlobalContext();

  const tariffsLink = useMemo(
    () =>
      isContentGeorgian(contentLanguage)
        ? "https://www.procreditbank.ge/ge/tables/%E1%83%A1%E1%83%AE%E1%83%95%E1%83%90-%E1%83%A1%E1%83%90%E1%83%91%E1%83%90%E1%83%9C%E1%83%99%E1%83%9D-%E1%83%9B%E1%83%9D%E1%83%9B%E1%83%A1%E1%83%90%E1%83%AE%E1%83%A3%E1%83%A0%E1%83%94%E1%83%91%E1%83%98%E1%83%A1-%E1%83%A2%E1%83%90%E1%83%A0%E1%83%98%E1%83%A4%E1%83%94%E1%83%91%E1%83%98?mid=773"
        : "https://www.procreditbank.ge/en/tables/other-banking-services-fees",
    [contentLanguage]
  );

  const { isPending, mutate: handleSubmitPiForm } = useMutation({
    mutationFn: (data) => {
      return Services.submitPIForm(data);
    },
    onSuccess: () => {
      setIsSendedForm(true);
      document.dispatchEvent(successEvent);
      sessionStorage.removeItem("localToken");
      sessionStorage.removeItem("onboardingToken");
      clearPersistedIdentificationStatus();
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("consentMarkPurpose");
      localStorage.removeItem("person");
    },

    onError: (err) => {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("localToken");
        clearPersistedIdentificationStatus();
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("consentMarkPurpose");
        resetGlobalData();
      }
      catchErrorPIOnboarding(err, {
        withoutRedirect: true,
      });
    },
  });

  const onSubmit = (data) => {
    const additionalInfo = {
      identomatSessionToken: token,
      ServiceType: SERVICE_TYPES.PIForm,
      client_type: clientTypeId,
      locale: localStorage.getItem("culture"),
      consentMarkPurpose: localStorage.getItem("consentMarkPurpose") === "true",
    };
    const transformedData = transformDataForSubmit(data);
    handleSubmitPiForm({ additionalInfo, data: transformedData });
  };

  function transformDataForSubmit(data) {
    if (!data) return;
    let temp = {};
    for (const [key, value] of Object.entries(data)) {
      if (YES_NO_QUESTIONS.some((item) => item === key)) {
        temp[key] = Number(value) === YES_NO_VAL.yes ? true : false;
      } else if (typeof value === "string") {
        if (value.trim() !== "") {
          temp[key] = value;
        }
      } else if (typeof value === "boolean") {
        temp[key] = value;
      } else if (Array.isArray(value)) {
        if (key === "tax_residency_countries") {
          let taxInfo = [];
          if (value?.length > 0) {
            value.forEach((item) => {
              if (item?.countryId?.value && item?.taxCode) {
                return taxInfo.push({
                  ["country_id"]: item?.countryId?.value,
                  ["tax_code"]: item?.taxCode,
                });
              }
            });
          }
          temp[key] = taxInfo;
        } else {
          temp[key] = value.map((item) => item?.value);
        }
      } else if (SUBFORM_PREFIXES.some((item) => item === key)) {
        const subformFields = transformDataForSubmit(value);
        if (subformFields && Object.keys(subformFields).length > 0) {
          temp[key] = subformFields;
        }
      } else if (typeof value === "object") {
        if (value?.hasOwnProperty("value")) {
          temp[key] = value["value"];
        }
      }
    }
    return temp;
  }

  const isDisabled = isPending;

  if (isDisabled) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (isSendedForm) {
    return (
      <div>
        <div className={"flex justify-center items-center"}>
          <img src={successIcon} width={100} height={100} alt="#" />
        </div>
        <TextWrapper className={"textsWrapper"}>
          <span className={"title"}>{t("completedSuccessfully")}</span>
          <div>{t("finishText")}</div>
          <div>{t("waitForMessage")}</div>
        </TextWrapper>
      </div>
    );
  }

  return (
    <StepWrapper className="flex flex-col gap-14">
      <DesktopOnly className="flex flex-wrap justify-center gap-4">
        <div className="flex flex-col items-center gap-2"></div>
      </DesktopOnly>

      <DesktopOnly>
        <div className="grid grid-cols-[80px_auto] gap-y-12">
          <div></div>

          <div>
            <span>
              <b>{t("minimal")}</b>
            </span>
            <a
              href={tariffsLink}
              className="text-[#e6000f] bold opacity-100"
              target="blank"
            >
              <span>{t("minimalAccount")}</span>
            </a>
            <span>
              <b>{t("getMinimal")}</b>
            </span>
          </div>
          <UserIcon width="60" className="" />
          <div className="flex flex-col gap-4">
            <p>
              <b>{t("currentAccount")}</b> - {t("currentyAccountDesc")}
            </p>
          </div>

          <ConversationIcon width="60" height="35" />
          <div>
            <p>
              <b>{t("smsService")}</b>
            </p>{" "}
          </div>

          <ComputerIcon width="60" />
          <div>
            <p>
              <b>{t("internetBank")}</b>
            </p>
          </div>
          <div></div>

          <div>
            <span>
              <b>{t("standart")}</b>
            </span>
            <a
              href={tariffsLink}
              className="text-[#e6000f] bold opacity-100"
              target="blank"
            >
              <span>{t("standartAccount")}</span>
            </a>{" "}
            <span>{t("forYou")}</span>
          </div>
        </div>
      </DesktopOnly>

      <MobileOnly>
        <MobileIntroText>
          <span>
            <b>{t("minimal")}</b>
          </span>
          <a href={tariffsLink} target="blank">
            <span>{t("minimalAccount")}</span>
          </a>
          <span>
            <b>{t("getMinimal")}</b>
          </span>
        </MobileIntroText>

        <MobileServicesList>
          <MobileServiceItem>
            <UserIcon width="34" />
            <div>
              <b>{t("currentAccount")}</b> - {t("currentyAccountDesc")}
            </div>
          </MobileServiceItem>

          <MobileServiceItem>
            <ConversationIcon width="34" height="28" />
            <div>
              <b>{t("smsService")}</b>
            </div>
          </MobileServiceItem>

          <MobileServiceItem>
            <ComputerIcon width="34" />
            <div>
              <b>{t("internetBank")}</b>
            </div>
          </MobileServiceItem>
        </MobileServicesList>

        <MobileIntroText>
          <span>
            <b>{t("standart")}</b>
          </span>
          <a href={tariffsLink} target="blank">
            <span>{t("standartAccount")}</span>
          </a>{" "}
          <span>{t("forYou")}</span>
        </MobileIntroText>
      </MobileOnly>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ButtonsWrapper>
          <Button
            type="button"
            onClick={(e) => {
              e?.preventDefault();
              history.goBack();
            }}
            variant="inherit"
          >
            {t("prev")}
          </Button>

          <Button
            disabled={isDisabled}
            type="submit"
            variant={isDisabled ? "disabled" : "primary"}
          >
            {t("finish")}
          </Button>
        </ButtonsWrapper>
      </form>
    </StepWrapper>
  );
}

export default AboutServices;
